<script setup lang="ts">
import { ref, watch } from "vue";
import { api, errorText, useSession } from "../api";
const session = useSession(),
  policy = ref<any>(),
  loading = ref(true),
  busy = ref(false),
  error = ref(""),
  notice = ref("");
let generation = 0;
async function load() {
  const current = ++generation;
  loading.value = true;
  error.value = "";
  policy.value = undefined;
  notice.value = "";
  try {
    const result = await api("/api/workspace/ai-policy");
    if (current === generation) policy.value = result;
  } catch (e) {
    if (current === generation) error.value = errorText(e);
  } finally {
    if (current === generation) loading.value = false;
  }
}
watch(() => session.organization, load, { immediate: true });
async function save() {
  busy.value = true;
  error.value = "";
  notice.value = "";
  const org = session.organization;
  try {
    const p = policy.value;
    await api("/api/workspace/ai-policy", {
      method: "PUT",
      body: {
        enabled: p.enabled,
        providerOrigin: p.providerOrigin ?? p.approvedOrigin ?? "",
        region: p.region ?? p.approvedRegion ?? "Ikke konfigureret",
        allowedClasses: p.allowedClasses,
        dailyTokens: p.dailyTokens,
        version: p.version,
      },
    });
    if (org === session.organization) {
      await load();
      notice.value = "AI-indstillingerne er gemt.";
    }
  } catch (e) {
    if (org === session.organization) error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
</script>
<template>
  <section class="ai-policy" aria-labelledby="ai-policy-title">
    <h2 id="ai-policy-title">AI-forslag i Yawhub</h2>
    <p class="muted">
      Styr den AI-tjeneste, Yawhub bruger til forslag og spørgsmål.
      Indstillingerne gælder hele workspacet.
    </p>
    <p v-if="loading" role="status">Henter AI-indstillinger…</p>
    <p v-if="error" class="error" role="alert">
      {{ error }} <button class="text-button" @click="load">Prøv igen</button>
    </p>
    <form v-if="policy" class="form-stack" @submit.prevent="save">
      <dl>
        <div>
          <dt>Udbyder</dt>
          <dd>{{ policy.providerOrigin ?? "Ikke tilsluttet" }}</dd>
        </div>
        <div>
          <dt>Region godkendt af driftsansvarlig</dt>
          <dd>{{ policy.region ?? "Ikke angivet" }}</dd>
        </div>
      </dl>
      <p v-if="!policy.configured" class="warning">
        AI-udbyder og region skal konfigureres af den driftsansvarlige, før AI
        kan aktiveres. Du kan fortsat arbejde manuelt.
      </p>
      <fieldset :disabled="!policy.canManage || busy">
        <legend>Adgang og data</legend>
        <label class="check-label"
          ><input
            v-model="policy.enabled"
            type="checkbox"
            :disabled="!policy.configured && !policy.enabled"
          />
          Tillad AI-forslag i dette workspace</label
        >
        <label class="check-label"
          ><input
            v-model="policy.allowedClasses"
            type="checkbox"
            value="structure"
          />
          Forslag til schema og nye felter</label
        >
        <p class="small muted">
          Kun anonymiserede feltnavne, typer og antal sendes. Eksempelrækker,
          felternes værdier og oprindelige feltnavne bliver i Yawhub.
        </p>
        <label class="check-label"
          ><input
            v-model="policy.allowedClasses"
            type="checkbox"
            value="question"
          />
          Spørgsmål til datasæt</label
        >
        <p class="small muted">
          Dit spørgsmål, feltnavne, typer og tidszone sendes. Genkendelige
          e-mailadresser, personnumre og nøgler maskeres. Undlad følsomme
          oplysninger i spørgsmål og feltnavne. Rækker sendes ikke.
        </p>
        <label
          >Dagligt tokenbudget<input
            v-model.number="policy.dailyTokens"
            type="number"
            min="4000"
            max="10000000"
            step="1000"
            required
        /></label>
        <p class="small muted">
          Højst to samtidige AI-kald. Budgettet reserverer et øvre tokenforbrug
          pr. kald, også hvis udbyderen fejler.
        </p>
      </fieldset>
      <p v-if="!policy.canManage" class="muted">
        En administrator kan ændre disse indstillinger.
      </p>
      <button v-else class="button primary" :disabled="busy || loading">
        {{ busy ? "Gemmer…" : "Gem AI-indstillinger" }}
      </button>
    </form>
    <p v-if="notice" class="success" role="status">{{ notice }}</p>
  </section>
</template>
<style scoped lang="scss" src="../styles/components/AiPolicy.scss"></style>
